import z from "zod";

/**
 * @see {@link https://github.com/lasso-x/lasso/blob/staging/Lasso.Std.Domain/Enumerations/ErrorType.cs}
 */
export enum LassoErrorCode {
  //Generic errors
  GenericLassoError = 1,
  GenericProviderError = 2,
  MissingArgumentError = 3,
  UnsupportedFeature = 4,
  UncaughtException = 5,
  InvalidArgumentError = 6,
  NotImplementedError = 7,
  TooMuchDataException = 8,
  CatastrophicFailure = 9,

  //Authentication errors
  AuthenticationError = 10,
  AuthenticationArgumentException = 11,
  ProviderAuthenticationError = 12,
  MultipleOrganizationsException = 13,
  LoginAttemptsExceededError = 14,
  InvalidCredentialsError = 15,
  UsernameExistsError = 16,
  EmailNotConfirmedError = 17,
  TermsNotAcceptedError = 18,
  UserEmailUnconfirmed = 19,
  RegisteredWithOtherProviderError = 110,
  TwoFactorRequired = 111,
  TwoFactorInvalidCode = 112,

  //Authorization errors
  AuthorizationError = 20,
  TokenExpiredError = 21,
  AppAuthorizationError = 22,
  ProviderAuthorizationError = 23,
  ReadOnlyAuthorizationError = 24,
  FeatureAuthorizationError = 25,
  ThrottlingError = 26,
  SocialLoginRequired = 27,

  //Warnings
  Warning = 30,

  //Not found errors
  NotFoundError = 40,
  ProductNotFoundError = 41,
  OrganizationNotFoundError = 42,
  AppNotFoundError = 43,
  UserNotFoundError = 44,
  LimitReachedError = 45,

  //Subscription errors
  SubscriptionError = 50,
  AppSubscriptionAlreadyActiveError = 51,
  ProductSubscriptionAlreadyActiveError = 52,
  AppSubscriptionNotFoundError = 53,
  ProductSubscriptionNotFoundError = 54,
  PaymentRequired = 55,
  AlreadyRegistered = 56,
  SubscriptionRecentlyExpired = 57,
  EmailDomainNotAllowed = 58,
  RecentTrialSubscription = 59,

  //License errors
  LicenseError = 60,
  OutOfLicensesError = 61,
  LicenseNotFoundError = 62,

  // Provider exceptions
  APIDisabled = 70,
  ProviderDisabled = 71,
  Timeout = 72,
  AzureServiceFailure = 73,

  //Payment failues
  PaymentFailed = 80,

  // Specific provider errors
  VIRKScrollExpired = 100,

  // Context-specific errors. These are used in specific contexts and should not be used elsewhere.
  // They are used to signal specific errors that are not generic enough to be in the generic error list.
  ProspectingDeliveryCancelled = 150,
  // TODO - duplicate enum value
  // ProspectingDeliveryFailed = 150,
}

export const lassoExceptionSchema = z.object({
  errorCode: z.nativeEnum(LassoErrorCode),
  errorMessage: z.string(),
  httpStatusCode: z.number(),
});

interface NamedErrorType<ErrorType extends string> extends Error {
  name: ErrorType;
}

interface NamedErrorTypeClass<
  ErrorTypes extends Record<string, (...args: unknown[]) => string>,
> {
  new <T extends Extract<keyof ErrorTypes, string>>(
    name: T,
    ...args: Parameters<ErrorTypes[T]>
  ): NamedErrorType<Extract<keyof ErrorTypes, string>>;
}

export function createNamedErrorType<
  ErrorTypes extends {
    [K in keyof ErrorTypes]: ErrorTypes[K] extends (
      ...args: infer Args
    ) => string
      ? (...args: Args) => string
      : never;
  },
>(types: ErrorTypes): NamedErrorTypeClass<ErrorTypes> {
  type ErrorType = Extract<keyof ErrorTypes, string>;

  return class<T extends ErrorType> extends Error {
    public name: ErrorType;

    constructor(name: T, ...args: Parameters<ErrorTypes[T]>) {
      const createMessage = types[name] as (
        ...args: Parameters<ErrorTypes[T]>
      ) => string;
      super(createMessage(...args));
      this.name = name;
    }
  };
}
